import { Box, Typography } from "@outschool/backpack";
import { CategoryPage, SearchFilters } from "@outschool/gql-backend-generated";
import {
  useLocale,
  useMachineTranslation,
  useTranslation,
} from "@outschool/localization";
import { createActivityPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { ReviewCard } from "@outschool/ui-components-website";
import {
  Carousel,
  Container,
  ImageWithModal,
  Loading,
} from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import omit from "lodash/omit";
import React, { useMemo } from "react";

import { useCuratedCategoryReviewsQuery } from "../hooks/useReviewsData";

const REVIEW_IMAGE_LIMIT = 8;
const REVIEW_LIMIT = 6;

function CuratedReviews({
  category,
  filters,
}: {
  category: CategoryPage;
  filters: SearchFilters;
}) {
  const { t } = useTranslation(
    "client\\components\\categories\\CuratedReviews",
  );

  const reviewFilters: SearchFilters = useMemo(() => {
    const combinedObj = omit(
      {
        ...filters,
        activityRatingMin: 4,
        order: "popular",
        isAvailable: true,
        age: null,
      },
      "__typename",
    );
    return Object.keys(combinedObj).reduce((obj, key) => {
      // @ts-ignore
      if (!!combinedObj[key]) {
        // @ts-ignore
        obj[key] = combinedObj[key];
      }
      return obj;
    }, {});
  }, [filters]);

  const { data, loading, error } = useCuratedCategoryReviewsQuery({
    searchFilters: reviewFilters,
    pageLocation: "CURATED_CATEGORY",
  });

  const hasPhotos = data?.feedbackForCategorySearchFilters?.hasPhotos?.filter(
    review => !!review?.activity,
  );
  const noPhotos = data?.feedbackForCategorySearchFilters?.noPhotos?.filter(
    review => !!review?.activity,
  );
  const searchUid =
    data?.feedbackForCategorySearchFilters?.searchUid ?? undefined;

  if (error || (!hasPhotos && !noPhotos)) {
    return <></>;
  }

  if (loading) {
    return <Loading delay={0} />;
  }

  return (
    <Container>
      {((hasPhotos && hasPhotos.length > 1) ||
        (noPhotos && noPhotos.length > 1)) && (
        <Typography emphasized variant="h4" sx={{ margin: "8px 0px" }}>
          {category.reviewTitleWithPhotos}
        </Typography>
      )}

      {hasPhotos && hasPhotos.length > 1 && (
        <Box>
          <Typography variant="h6" sx={{ color: "grey.600", margin: "0px" }}>
            {t("Parent submitted images")}
          </Typography>

          <PhotoReviews
            reviews={hasPhotos}
            categorySlug={category.slug}
            searchUid={searchUid}
          />

          <Typography
            variant="h6"
            sx={{
              color: "grey.600",
              margin: "0px",
              marginTop: "8px",
              marginBottom: "8px",
            }}
          >
            {category.reviewTitle}
          </Typography>
        </Box>
      )}
      {noPhotos && <WrittenReviews reviews={noPhotos} searchUid={searchUid} />}
    </Container>
  );
}

export default React.memo(CuratedReviews);

export const PhotoReviews = ({
  reviews,
  categorySlug,
  hideCaption = false,
  searchUid,
}: {
  reviews: any[];
  categorySlug?: string;
  hideCaption?: boolean;
  searchUid?: string;
}) => {
  const isMobile = useIsMobile();
  const width = "100%";
  const size = isMobile ? "144px" : "176px";
  const trackEvent = useTrackEvent();

  return (
    <Carousel leftAlign={true}>
      {reviews.slice(0, REVIEW_IMAGE_LIMIT).map(review => (
        <Box
          key={review.uid}
          sx={{
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 0,
            paddingRight: 14,
          }}
        >
          <ImageWithModal
            onClick={() => {
              // eslint-disable-next-line i18next/no-literal-string
              categorySlug &&
                trackEvent("category_review_image_click", {
                  reviewUID: review.uid,
                  categorySlug,
                  searchUid,
                });
            }}
            imageSize={isMobile ? 144 : 176}
            imageUrl={review.attachments[0].file.url}
            imageSx={{
              width: size,
              height: size,
              objectFit: "cover", // eslint-disable-line i18next/no-literal-string
            }}
            caption={
              !hideCaption ? (
                <Review
                  review={review}
                  showGrayCard={false}
                  width={width}
                  useShowMore={false}
                />
              ) : null
            }
            showDownload={false}
          />
        </Box>
      ))}
    </Carousel>
  );
};

export const WrittenReviews = ({
  reviews,
  reviewOverrideLimit,
  searchUid,
}: {
  reviews: any[];
  reviewOverrideLimit?: number;
  searchUid?: string;
}) => {
  const isMobile = useIsMobile();
  return (
    <Carousel
      leftAlign={true}
      innerSx={{
        gap: isMobile ? 16 : 35,
      }}
    >
      {reviews.slice(0, reviewOverrideLimit || REVIEW_LIMIT).map(review => (
        <Box key={review.uid}>
          <Review
            review={review}
            showGrayCard={true}
            width={isMobile ? 284 : 440}
            useShowMore={true}
            searchUid={searchUid}
          />
        </Box>
      ))}
    </Carousel>
  );
};

export const Review = ({
  review,
  showGrayCard,
  width,
  useShowMore,
  searchUid,
}: {
  review: any;
  showGrayCard: boolean;
  width: string | number;
  useShowMore: boolean;
  searchUid?: string;
}) => {
  const { translate: googleT } = useMachineTranslation();
  const locale = useLocale();
  const { t } = useTranslation(
    "client\\components\\categories\\CuratedReviews",
  );

  const date = new Date(review.created_at).toLocaleDateString(locale, {
    month: "short", // eslint-disable-line i18next/no-literal-string
    day: "numeric", // eslint-disable-line i18next/no-literal-string
  });

  const classPath = createActivityPath(
    review.activity.title,
    review.activity.slug_id,
    review.activity.isClub,
  );

  return (
    <ReviewCard
      starRating={review.activity_star_rating}
      reviewContent={googleT(review.details.publicReview)}
      reviwerName={review.parent?.publicName}
      reviewDate={t("on {{date}}", { date })}
      reviewerImageURL={review.parent?.photo}
      activityTitle={review.activity.titleTranslated}
      activityRoute={classPath}
      showGrayCard={showGrayCard}
      width={width}
      maxCharactersForShowMoreButton={123}
      useShowMore={useShowMore}
      trackingUniqueId={review.uid}
      searchUid={searchUid}
    />
  );
};
